import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const META = {
  journal: { title: "Journal Entry", hint: "Debit one account, credit another — for adjustments and transfers of value" },
  payment: { title: "Payment Entry", hint: "Money you paid out — to a supplier, transporter or for an expense" },
  receipt: { title: "Receipt Entry", hint: "Money you received — usually from a customer" },
};

const today = () => new Date().toISOString().slice(0, 10);
const rs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function Vouchers({ voucherType }) {
  const meta = META[voucherType];
  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: today(), ref_no: "", party: "", amount: "", mode: "cash",
                                     debit_account: "", credit_account: "", narration: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/vouchers", { params: { type: voucherType } })
      .then(({ data }) => setRows(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    setForm({ date: today(), ref_no: "", party: "", amount: "", mode: "cash",
              debit_account: "", credit_account: "", narration: "" });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherType]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/vouchers", { ...form, type: voucherType, amount: parseFloat(form.amount) || 0 });
      toast.success(`${meta.title} saved`);
      setForm({ date: today(), ref_no: "", party: "", amount: "", mode: "cash",
                debit_account: "", credit_account: "", narration: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vouchers/${id}`);
      toast.success("Entry removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1200px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Accounts</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">{meta.title}</h1>
          <p className="mt-2 text-sm text-zinc-500">{meta.hint}</p>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-3">
          <div>
            <L>Date</L>
            <DateInput data-testid="voucher-date-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={form.date} onChange={set("date")} />
          </div>
          <div>
            <L>Voucher no.</L>
            <Input data-testid="voucher-ref-input" className="mt-2 rounded-sm" value={form.ref_no} onChange={set("ref_no")} placeholder="e.g. RCP-001" />
          </div>
          <div>
            <L>Amount ₹ *</L>
            <Input data-testid="voucher-amount-input" required type="number" step="any" className="mt-2 rounded-sm" value={form.amount} onChange={set("amount")} />
          </div>

          {voucherType === "journal" ? (
            <>
              <div>
                <L>Debit account *</L>
                <Input data-testid="voucher-debit-input" required className="mt-2 rounded-sm" value={form.debit_account} onChange={set("debit_account")} placeholder="e.g. Discount Allowed" />
              </div>
              <div>
                <L>Credit account *</L>
                <Input data-testid="voucher-credit-input" required className="mt-2 rounded-sm" value={form.credit_account} onChange={set("credit_account")} placeholder="e.g. SHREE CLOTH HOUSE" />
              </div>
            </>
          ) : (
            <>
              <div>
                <L>{voucherType === "receipt" ? "Received from *" : "Paid to *"}</L>
                <Input data-testid="voucher-party-input" required className="mt-2 rounded-sm" value={form.party} onChange={set("party")} placeholder="Party name" />
              </div>
              <div>
                <L>Mode</L>
                <Input data-testid="voucher-mode-input" className="mt-2 rounded-sm" value={form.mode} onChange={set("mode")} placeholder="cash / upi / cheque / rtgs" />
              </div>
            </>
          )}

          <div className="sm:col-span-2">
            <L>Narration</L>
            <Input data-testid="voucher-narration-input" className="mt-2 rounded-sm" value={form.narration} onChange={set("narration")} placeholder="What is this entry for?" />
          </div>
          <div className="flex items-end">
            <Button data-testid="voucher-save-button" data-f2-save type="submit" disabled={saving} className="w-full rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90">
              <Plus size={15} className="mr-2" /> {saving ? "Saving…" : "Save entry"}
            </Button>
          </div>
        </form>

        {rows.length === 0 ? (
          <div data-testid="voucher-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">No entries yet</h3>
            <p className="mt-1 text-sm text-zinc-500">Saved entries will be listed here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 bg-white" data-testid="voucher-table">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200">
                <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Voucher no.</th>
                  <th className="px-4 py-2 text-left">{voucherType === "journal" ? "Debit / Credit" : "Party"}</th>
                  <th className="px-4 py-2 text-left">{voucherType === "journal" ? "Narration" : "Mode"}</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`voucher-row-${r.id}`} className="hover:bg-zinc-50">
                    <td className="font-mono-num px-4 py-2">{toDMY(r.date)}</td>
                    <td className="px-4 py-2 font-semibold">{r.ref_no || "-"}</td>
                    <td className="px-4 py-2">
                      {voucherType === "journal" ? `${r.debit_account} → ${r.credit_account}` : r.party}
                    </td>
                    <td className="px-4 py-2 text-zinc-500">
                      {voucherType === "journal" ? r.narration || "-" : r.mode}
                    </td>
                    <td className="font-mono-num px-4 py-2 text-right font-semibold">{rs(r.amount)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`voucher-delete-${r.id}`}
                        onClick={() => remove(r.id)}
                        className="text-zinc-400 transition-colors duration-200 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-zinc-300 bg-zinc-50">
                <tr className="text-sm font-semibold">
                  <td className="px-4 py-2" colSpan={4}>
                    Total ({rows.length} entries)
                  </td>
                  <td className="font-mono-num px-4 py-2 text-right" data-testid="voucher-total">{rs(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </main>
    </AppShell>
  );
}
